import React, { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
// --------------------------------------------------------------------------------
import { String } from '../String';
import { InputProps } from '../Input';
import { config, Icons } from '../../index';

export interface CheckBoxProps extends InputProps {
  /* Checkbox state */
  isChecked?: boolean;
  /* Checkbox margin */
  margin?: string;
  /* Checkbox padding */
  padding?: string;
  // --------------------------------------------------------------------------------
  /* Checkbox onChange handler */
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

interface PrivateCheckBoxProps extends CheckBoxProps {
  checked?: boolean;
}

const CheckboxWrapper = styled.div<CheckBoxProps>`
  padding: ${(props) => props.padding};
  margin: ${(props) => props.margin};
`;

const CheckboxContainer = styled.div<PrivateCheckBoxProps>`
  display: flex;
  align-items: center;
  padding: ${config.spacingSmall} ${config.spacingXLarge};
  border-radius: ${(props) => props.borderRadius};
  height: ${(props) => props.height};
  width: ${(props) => props.width};
  background-color: ${(props) =>
    props.checked ? props.backgroundColor : config.muted};
  transition: ${config.transitionShort};
  cursor: pointer;
  user-select: none;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  overflow: hidden;
  white-space: nowrap;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
`;

const StyledCheckbox = styled.label<PrivateCheckBoxProps>`
  cursor: pointer;
  width: ${config.spacingXXLarge};
  height: ${config.spacingXXLarge};
  background: ${config.catskillWhite};
  color: ${config.catskillWhite};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// 📌 Primary UI component for user interaction
export const CheckBox: React.FC<CheckBoxProps> = ({
  label,
  isChecked = false,
  width = 'fit-content',
  height = config.spacingXXLarge,
  borderRadius = '30px',
  margin,
  padding,
  backgroundColor = config.primary,
  border = `1px solid ${config.muted}`,
  name,
  // --------------------------------------------------------------------------------
  onChange,
  // additional props
  ...props
}) => {
  const [checked, setChecked] = useState(isChecked);

  const handleCheckboxChange = () => {
    // pass onChange handler to parent
    onChange?.({
      target: {
        value: !checked,
        name: name || '_',
      },
      isTrusted: true,
    } as any);

    setChecked(!checked);
  };

  return (
    <CheckboxWrapper margin={margin} padding={padding}>
      <CheckboxContainer
        width={width}
        height={height}
        backgroundColor={backgroundColor}
        border={border}
        borderRadius={borderRadius}
        checked={checked}
        onClick={handleCheckboxChange}
        {...props}
      >
        <HiddenCheckbox type="checkbox" checked={checked} onChange={() => {}} />
        <StyledCheckbox checked={checked}>
          <Icons
            icon="tick"
            fill={checked ? backgroundColor : config.transparent}
          />
        </StyledCheckbox>
        <String
          /* 👉 label default props */
          color={checked ? config.white : config.darkMuted}
          fontSize={config.fontMedium}
          padding={`0 0 0 ${config.spacingLarge}`}
          fontWeight="400"
          limit={1}
          cursor="pointer"
          {...label}
        />
      </CheckboxContainer>
    </CheckboxWrapper>
  );
};
